<script lang="ts" generics="MODEL_VALUE">
    import {getInjector, getSanitize} from 'src/core';
    import type {KpUniversalFormComponentOptions, KpUniversalFormSettings} from './types';
    import type {ObjectValueEditorLabelsWidth, ObjectValueEditorOptions} from '../internal/meta-editors/object/types';
    import type {FormSubmitRequestEvent} from 'shared/value-editors/events';
    import type {FormController, FormControlOptions} from '../internal/forms/types';
    import type {FormGroup} from '../internal/forms/form-group';
    import {setContext} from 'svelte';
    import {ValueEditorSize} from 'shared/value-editors/types';
    import {KpUniversalFormConfigurationService} from './kp-universal-form-configuration.service';
    import {uuid as generateUuid} from 'shared/utils/custom-utils';
    import {writable} from 'svelte/store';
    import {UNIVERSAL_FORM_MODEL} from 'shared/value-editors/context-keys';
    import KpValueEditor from '../kp-value-editor/KpValueEditor.svelte';

    export let model: MODEL_VALUE;
    export let formControlOptions: FormControlOptions | null = null;

    export let formSettings: KpUniversalFormSettings<MODEL_VALUE>;
    export let formId: string = generateUuid();
    export let formName = 'universalForm';

    export let size: ValueEditorSize = ValueEditorSize.MD;
    export let labelsWidth: ObjectValueEditorLabelsWidth | null = null;
    export let forceShowErrors = false;
    export let options: KpUniversalFormComponentOptions = {};

    let formRef: HTMLFormElement;
    let objectValueEditor: {getFormController: () => FormController<MODEL_VALUE>};

    const sanitizeHtml = getSanitize();
    const configuration = getInjector().getByClass(KpUniversalFormConfigurationService);

    const reactiveModel = writable(model);
    $: reactiveModel.set(model);
    setContext(UNIVERSAL_FORM_MODEL, reactiveModel);

    function resolveAutofocusFirstField(): boolean {
        return options?.autofocusFirstField ?? configuration.autofocusFirstField;
    }

    function createOptions(): ObjectValueEditorOptions<MODEL_VALUE> {
        return {fields: formSettings.fields, ...labelsWidth ? {labelsWidth} : {}};
    }

    function submitManually(event: FormSubmitRequestEvent) {
        const submitEvent = new SubmitEvent('submit', {
            bubbles: true,
            cancelable: true,
            submitter: event.detail.submitter
        });
        formRef.dispatchEvent(submitEvent);
    }

    export function getFormController(): FormGroup<MODEL_VALUE> {
        return (objectValueEditor as unknown as {getFormController: () => FormGroup<MODEL_VALUE>}).getFormController();
    }
</script>

<form bind:this={formRef} id={formId} name={formName} on:submit|preventDefault novalidate>
    <slot name="before-header"></slot>

    {#if formSettings.header}
        <p class="text-info text-info-up">
            {@html sanitizeHtml(formSettings.header)}
        </p>
    {/if}

    <slot name="after-header"></slot>

    <!-- if any of these settings changes destroy form and create a new one -->
    {#key formSettings || labelsWidth}
        <KpValueEditor
            type="object"

            bind:model
            {formControlOptions}

            editorId={null}
            editorName={formName}

            {size}
            isFocused={resolveAutofocusFirstField()}
            {forceShowErrors}
            options={createOptions()}

            on:model-change
            on:form-submit-request={submitManually}

            bind:this={objectValueEditor}/>
    {/key}

    <slot name="before-footer"></slot>

    {#if formSettings.footer}
        <p class="text-info text-info-bottom">
            {@html sanitizeHtml(formSettings.footer)}
        </p>
    {/if}

    <slot name="after-footer"></slot>

    <!-- invisible submit button - due to html specification, form doesn't submit by enter when submit button is missing-->
    <!-- see https://github.com/angular/angular.js/issues/6017#issuecomment-50808489-->
    <button type="submit" style="display: none;">ok</button>
</form>